// Base Tenant interface based on OpenAPI schema
export interface Tenant {
    id: string; // MongoDB ObjectId
    name: string;
    email: string;
    full_address: string;
    privacy_officer?: string | null;
    partner_fee_cents?: number | null;
    language: 'en' | 'de';
    meta?: Record<string, any> | null;
    client_general_settings: Record<string, any>[];
    status: string;
    host: string;
    third_party_token: string;
    disabled_at?: string | null;
    organizationId: string;
    metadata: {
        created_at: string;
        updated_at: string;
        updated_by: string;
        user_id: string;
    };
}

// Request payload for creating a new tenant
export interface TenantCreateRequest {
    name: string;
    email: string;
    full_address: string;
    privacy_officer?: string | null;
    partner_fee_cents?: number | null;
    meta?: Record<string, any> | null;
    client_general_settings?: Record<string, any>[] | null;
    language?: 'en' | 'de';
}

// Response when creating a new tenant
export interface TenantCreateResponse {
    id: number; // External tenant ID (integer)
    name: string;
    email: string;
    full_address: string;
    privacy_officer?: string | null;
    partner_fee_cents?: number | null;
    meta?: Record<string, any> | null;
    client_general_settings: Record<string, any>[];
    status: string;
    created_at: string;
    updated_at: string;
    host: string;
    third_party_token: string;
}

// Request payload for updating a tenant (only meta field allowed)
export interface TenantUpdateRequest {
    meta?: Record<string, any> | null;
}

// Response wrapper for single tenant operations
export interface TenantResponse {
    data: Tenant;
}

// Response wrapper for tenant update operations
export interface TenantUpdateResponse {
    data: Tenant;
    message: string;
}

// Response wrapper for listing tenants
export interface TenantsListResponse {
    data: Tenant[];
    total: number;
}

// WhatsApp and Facebook onboarding types
export interface OnboardingData {
    custom_entry_point_url: string;
    custom_callback_url: string;
    partner_id: string;
}

export interface OnboardingResponse {
    data: OnboardingData;
    tenant: {
        id: string;
        name: string;
    };
}

// Agent interface for WhatsApp and Facebook connection status
export interface Agent {
    id: number;
    channel: 'whatsapp' | 'facebook' | 'bridge' | 'widget';
    display_id: string;
    name: string;
    in_sync: boolean;
    unread_conversations_count: number;
}

// Third Party Settings types
export interface ThirdPartySettings {
    webhooks_enabled: boolean;
    webhook_url?: string | null;
    webhook_secret?: string | null;
    email_landing_url?: string | null;
}

export interface ThirdPartySettingsResponse {
    data: ThirdPartySettings;
}

export interface ThirdPartySettingsUpdateRequest {
    webhooks_enabled?: boolean;
    webhook_url?: string | null;
    webhook_secret?: string | null;
    email_landing_url?: string | null;
}

// WhatsApp Template types
export interface WhatsAppTemplateComponent {
    type: string;
    format?: string | null;
    text?: string | null;
    buttons?: object[] | null;
    example?: object | null;
    [key: string]: any; // For additional properties
}

export interface WhatsAppTemplate {
    id: number;
    agent_ids: number[];
    group?: string | null;
    template_id: string;
    namespace: string;
    name: string;
    friendly_name?: string | null;
    language: string;
    category: 'AUTHENTICATION' | 'MARKETING' | 'UTILITY';
    status: 'created' | 'submitted' | 'approved' | 'in_appeal' | 'pending' | 'rejected' | 'pending_deletion' | 'deleted' | 'disabled' | 'limit_exceeded';
    components: WhatsAppTemplateComponent[];
}

export interface WhatsAppTemplateListResponse {
    data: WhatsAppTemplate[];
}

export interface WhatsAppTemplateResponse {
    data: WhatsAppTemplate;
}

export interface WhatsAppTemplateCreateRequest {
    name: string;
    friendly_name?: string | null;
    group?: string | null;
    category: 'AUTHENTICATION' | 'MARKETING' | 'UTILITY';
    components: WhatsAppTemplateComponent[];
    agent_id?: number | null;
    language: string;
}

export interface WhatsAppTemplateDeleteResponse {
    data: object[];
}

// WhatsApp supported languages for templates
export const WHATSAPP_TEMPLATE_LANGUAGES = [
    { code: "af", name: "Afrikaans" },
    { code: "sq", name: "Albanian" },
    { code: "ar", name: "Arabic" },
    { code: "az", name: "Azerbaijani" },
    { code: "bn", name: "Bengali" },
    { code: "bg", name: "Bulgarian" },
    { code: "ca", name: "Catalan" },
    { code: "zh_CN", name: "Chinese (CHN)" },
    { code: "zh_HK", name: "Chinese (HKG)" },
    { code: "zh_TW", name: "Chinese (TAI)" },
    { code: "hr", name: "Croatian" },
    { code: "cs", name: "Czech" },
    { code: "da", name: "Danish" },
    { code: "nl", name: "Dutch" },
    { code: "en", name: "English" },
    { code: "en_GB", name: "English (UK)" },
    { code: "en_US", name: "English (US)" },
    { code: "et", name: "Estonian" },
    { code: "fil", name: "Filipino" },
    { code: "fi", name: "Finnish" },
    { code: "fr", name: "French" },
    { code: "ka", name: "Georgian" },
    { code: "de", name: "German" },
    { code: "el", name: "Greek" },
    { code: "gu", name: "Gujarati" },
    { code: "ha", name: "Hausa" },
    { code: "he", name: "Hebrew" },
    { code: "hi", name: "Hindi" },
    { code: "hu", name: "Hungarian" },
    { code: "id", name: "Indonesian" },
    { code: "ga", name: "Irish" },
    { code: "it", name: "Italian" },
    { code: "ja", name: "Japanese" },
    { code: "kn", name: "Kannada" },
    { code: "kk", name: "Kazakh" },
    { code: "rw_RW", name: "Kinyarwanda" },
    { code: "ko", name: "Korean" },
    { code: "ky_KG", name: "Kyrgyz (Kyrgyzstan)" },
    { code: "lo", name: "Lao" },
    { code: "lv", name: "Latvian" },
    { code: "lt", name: "Lithuanian" },
    { code: "mk", name: "Macedonian" },
    { code: "ms", name: "Malay" },
    { code: "ml", name: "Malayalam" },
    { code: "mr", name: "Marathi" },
    { code: "nb", name: "Norwegian" },
    { code: "fa", name: "Persian" },
    { code: "pl", name: "Polish" },
    { code: "pt_BR", name: "Portuguese (BR)" },
    { code: "pt_PT", name: "Portuguese (POR)" },
    { code: "pa", name: "Punjabi" },
    { code: "ro", name: "Romanian" },
    { code: "ru", name: "Russian" },
    { code: "sr", name: "Serbian" },
    { code: "sk", name: "Slovak" },
    { code: "sl", name: "Slovenian" },
    { code: "es", name: "Spanish" },
    { code: "es_AR", name: "Spanish (ARG)" },
    { code: "es_ES", name: "Spanish (SPA)" },
    { code: "es_MX", name: "Spanish (MEX)" },
    { code: "sw", name: "Swahili" },
    { code: "sv", name: "Swedish" },
    { code: "ta", name: "Tamil" },
    { code: "te", name: "Telugu" },
    { code: "th", name: "Thai" },
    { code: "tr", name: "Turkish" },
    { code: "uk", name: "Ukrainian" },
    { code: "ur", name: "Urdu" },
    { code: "uz", name: "Uzbek" },
    { code: "vi", name: "Vietnamese" },
    { code: "zu", name: "Zulu" }
] as const; 